import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  ArchiveRestore,
  RefreshCw,
  AlertCircle,
  X,
  Search,
  Download,
  CheckSquare,
  FileText,
  Eye,
  PauseCircle,
  BookOpen,
  HelpCircle,
  Activity,
} from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { exportToExcel, formatDataForExport } from '../utils/excelExport';

const TABS = [
  {
    key: 'waiting',
    label: 'Waiting for Approval',
    icon: Clock,
    color: 'text-yellow-600',
  },
  {
    key: 'approved',
    label: 'Approved',
    icon: CheckCircle,
    color: 'text-green-600',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    icon: XCircle,
    color: 'text-red-600',
  },
  {
    key: 'recent',
    label: 'Rip Completed',
    icon: ArchiveRestore,
    color: 'text-purple-600',
  },
];

const ITEMS_PER_PAGE_OPTIONS = [25, 50, 100];

const formatDate = (isoString) => {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const SECTION_TABS = [
  { key: 'approvals', label: 'Approvals', icon: CheckSquare },
  { key: 'prepress', label: 'Prepress QC', icon: FileText },
  { key: 'ripqc', label: 'RIP QC', icon: Eye },
  { key: 'overall', label: 'Overall Status', icon: Activity },
];

// Per-project task stepper: each active project shows its own enabled tasks as
// a numbered pipeline — completed green, current highlighted, rejected red,
// remaining grey (like the reference stepper).
const ProjectStepper = ({ steps }) => {
  const circleCls = {
    completed:   'border-green-500 bg-white text-green-600',
    rejected:    'border-red-500 bg-red-500 text-white',
    in_progress: 'border-blue-600 bg-blue-600 text-white ring-4 ring-blue-100',
    on_hold:     'border-amber-500 bg-amber-500 text-white',
    pending:     'border-gray-300 bg-white text-gray-400',
  };
  const labelCls = {
    completed: 'text-green-700', rejected: 'text-red-600',
    in_progress: 'text-blue-700 font-semibold', on_hold: 'text-amber-700',
    pending: 'text-gray-400',
  };
  // connector before step i takes the colour of the PREVIOUS step's state
  const connCls = (prev) => {
    if (!prev) return 'bg-gray-200';
    if (prev.status === 'completed') return 'bg-green-500';
    if (prev.status === 'rejected') return 'bg-red-400';
    if (prev.status === 'in_progress') return 'bg-blue-500';
    if (prev.status === 'on_hold') return 'bg-amber-400';
    return 'bg-gray-200';
  };

  return (
    <div className="overflow-x-auto">
      <div className="flex items-start min-w-max pb-1">
        {steps.map((s, i) => {
          const state = s.current && s.status === 'pending' ? 'in_progress' : s.status;
          const cls = circleCls[state] || circleCls.pending;
          const showCheck = s.status === 'completed';
          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <div className="flex items-center pt-5 flex-1 min-w-[20px]">
                  <div className={`h-0.5 w-full ${connCls(steps[i - 1])}`} />
                </div>
              )}
              <div className="flex flex-col items-center w-28 shrink-0">
                <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-semibold ${cls} ${s.current ? 'ring-4 ring-blue-100' : ''}`}>
                  {showCheck ? <CheckCircle className="w-5 h-5" /> : i + 1}
                </div>
                <div className={`mt-2 text-[11px] text-center leading-tight ${s.current ? 'text-blue-700 font-semibold' : labelCls[s.status]}`}>
                  {s.name}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// Overall Status: one stepper per active project, based on its enabled tasks.
const OverallStatusSection = ({ token }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const PER_PAGE = 10;

  const load = React.useCallback(async (q = '', pg = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/dashboard/overall-status', {
        headers: { Authorization: `Bearer ${token}` },
        params: { ...(q ? { search: q } : {}), page: pg, limit: PER_PAGE },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load overall status');
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => { load(search, page); }, [load, page]); // eslint-disable-line

  const projects = data?.projects || [];
  const totalPages = data?.total_pages || 1;

  const doSearch = () => { setPage(1); load(search, 1); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-gray-600">
          <span className="font-semibold text-gray-800">{data?.active_projects || 0}</span> active projects
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-2.5 py-1 text-sm border border-gray-300 rounded-lg bg-white disabled:opacity-40 hover:bg-gray-50"
            >Prev</button>
            <span className="text-sm text-gray-600">Page {page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-2.5 py-1 text-sm border border-gray-300 rounded-lg bg-white disabled:opacity-40 hover:bg-gray-50"
            >Next</button>
          </div>
        )}
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }}
            placeholder="Search job code / project / client"
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg w-72"
          />
        </div>
        <button
          onClick={doSearch}
          className="flex items-center px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-gray-500 animate-pulse">Loading…</div>
      ) : error ? (
        <div className="py-6 px-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>
      ) : projects.length === 0 ? (
        <div className="py-10 text-center text-gray-500 border border-dashed border-gray-200 rounded-lg">
          No active projects to show.
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((p) => (
            <div key={p.project_id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div>
                  <div className="font-semibold text-gray-800">{p.job_code ? `#${p.job_code}` : 'No job code'}</div>
                  <div className="text-xs text-gray-500">
                    {p.title || 'Untitled'}
                    {p.client && <span> · {p.client}</span>}
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-800">{p.percent}%</span> · {p.done}/{p.total} tasks
                </div>
              </div>
              <ProjectStepper steps={p.steps} />
            </div>
          ))}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white disabled:opacity-40 hover:bg-gray-50"
              >Prev</button>
              <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white disabled:opacity-40 hover:bg-gray-50"
              >Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending:     { label: 'Pending',     cls: 'bg-yellow-100 text-yellow-700' },
    not_started: { label: 'Not Started', cls: 'bg-gray-100 text-gray-700' },
    in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-700' },
    paused:      { label: 'Paused',      cls: 'bg-orange-100 text-orange-700' },
    on_hold:     { label: 'On Hold',     cls: 'bg-red-100 text-red-700' },
    query:       { label: 'Query',       cls: 'bg-purple-100 text-purple-700' },
    completed:   { label: 'Completed',   cls: 'bg-green-100 text-green-700' },
  };
  const s = map[status] || { label: status || '-', cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`px-2 py-1 ${s.cls} rounded-full text-xs font-medium whitespace-nowrap`}>
      {s.label}
    </span>
  );
};

const TRACKER_TABS = [
  { key: 'pending',     label: 'Pending',     icon: Clock,        color: 'text-yellow-600' },
  { key: 'in_progress', label: 'In Progress', icon: RefreshCw,    color: 'text-blue-600' },
  { key: 'paused',      label: 'Paused',      icon: PauseCircle,  color: 'text-orange-500' },
  { key: 'on_hold',     label: 'On Hold',     icon: BookOpen,     color: 'text-red-500' },
  { key: 'query',       label: 'Query',       icon: HelpCircle,   color: 'text-purple-600' },
  { key: 'completed',   label: 'Completed',   icon: CheckCircle,  color: 'text-green-600' },
];

const TaskTrackerSection = ({ taskName, token }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 1 });
  const abortControllerRef = useRef(null);

  // Debounce search input — only update the value sent to the server after the user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchTasks = useCallback(async (tab, page, limit, searchVal, from, to) => {
    // Cancel the previous in-flight request so stale responses never overwrite fresh data
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ task_name: taskName, tab, page, limit });
      if (searchVal) params.set('search', searchVal);
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      const res = await api.get(`/pm/task-tracker?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortControllerRef.current.signal,
      });
      setTasks(res.data.tasks || []);
      setPagination(res.data.pagination || { total: 0, total_pages: 1 });
      setLoading(false);
    } catch (err) {
      // Ignore cancellations — a newer request is already in flight
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      setError(err.response?.data?.error || 'Failed to fetch tasks');
      setLoading(false);
    }
  }, [token, taskName]);

  // Single effect: re-fetches from the server whenever any filter or pagination param changes
  useEffect(() => {
    fetchTasks(activeTab, currentPage, itemsPerPage, debouncedSearch, dateFrom, dateTo);
  }, [activeTab, currentPage, itemsPerPage, debouncedSearch, dateFrom, dateTo, fetchTasks]);

  // Abort any pending request when the component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const totalPages = pagination.total_pages || 1;
  const totalCount = pagination.total || 0;
  const hasFilters = search || dateFrom || dateTo;

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const renderPageButtons = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push('...'); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push('...'); pages.push(totalPages); }
    return pages.map((p, idx) =>
      p === '...' ? (
        <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-500 text-sm">...</span>
      ) : (
        <button
          key={p}
          onClick={() => setCurrentPage(p)}
          className={`px-3 py-1 rounded text-sm font-medium border transition-colors ${
            p === currentPage ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >{p}</button>
      )
    );
  };

  return (
    <div>
      {/* Sub-Tabs */}
      <div className="flex items-end space-x-2 mb-4 border-b border-gray-200">
        {TRACKER_TABS.map(({ key, label, icon: Icon, color }) => (
          <button
            key={key}
            onClick={() => { setActiveTab(key); setCurrentPage(1); }}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === key ? 'border-blue-600 text-blue-600' : `border-transparent ${color} hover:border-gray-300`
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={() => fetchTasks(activeTab, currentPage, itemsPerPage, debouncedSearch, dateFrom, dateTo)}
          className="flex items-center px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors mb-1"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </button>
      </div>

      {/* Filter Bar */}
      <div className={`mb-4 p-3 rounded-lg border flex flex-wrap items-center gap-3 ${hasFilters ? 'bg-sky-100 border-sky-300' : 'bg-white border-gray-200'}`}>
        {hasFilters && (
          <button onClick={clearFilters} className="px-3 py-2 text-sm text-white bg-red-500 hover:bg-red-700 rounded-md transition-colors">
            Clear
          </button>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search job code, client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[220px]"
          />
        </div>
        <label className="text-sm text-gray-600 whitespace-nowrap">From:</label>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <label className="text-sm text-gray-600 whitespace-nowrap">To:</label>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 flex items-center rounded">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 font-medium">Job Code</th>
                  <th className="px-6 py-3 font-medium">Client</th>
                  <th className="px-6 py-3 font-medium">Complexity</th>
                  <th className="px-6 py-3 font-medium">Assigned To</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">In Time</th>
                  <th className="px-6 py-3 font-medium">Out Time</th>
                  {activeTab === 'completed' && (
                    <>
                      <th className="px-6 py-3 font-medium">Completed By</th>
                      <th className="px-6 py-3 font-medium">Completed At</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tasks.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-16 text-center text-gray-500">
                      No entries found.
                    </td>
                  </tr>
                ) : (
                  tasks.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-mono font-medium text-gray-700">{t.job_code || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{t.client || '-'}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium whitespace-nowrap capitalize">
                          {(t.complexity || '-').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{t.assigned_user_name || '-'}</td>
                      <td className="px-6 py-4 text-sm"><StatusBadge status={t.status} /></td>
                      <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">{formatDate(t.in_time)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">{formatDate(t.out_time)}</td>
                      {activeTab === 'completed' && (
                        <>
                          <td className="px-6 py-4 text-sm text-gray-700">{t.completed_by_name || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">{formatDate(t.completed_at)}</td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span className="text-sm text-gray-500">
                {totalCount > 0
                  ? `${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, totalCount)} of ${totalCount}`
                  : '0 results'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">«</button>
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">‹</button>
              {renderPageButtons()}
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">›</button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
                className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">»</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OnApprovals = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('waiting');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, reason: '', datetime: '', externalCategories: [] });
  const [approveModal, setApproveModal] = useState({ open: false, id: null, datetime: '' });
  const [actionLoading, setActionLoading] = useState(null);

  // External error categories for the reject dialog (same source as Active Projects).
  const [errorCategories, setErrorCategories] = useState([]);
  const [errorCategoriesLoading, setErrorCategoriesLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 1 });

  const searchTimer = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [sectionTab, setSectionTab] = useState('approvals');

  const canApproveReject = user && ['pm', 'admin', 'preflight_lead', 'cmd_lead', 'prepress_lead',
    'qc_lead', 'production_lead', 'epson_lead'].includes(user.role);
  const hasFilters = search || dateFrom || dateTo;

  const fetchNotifications = useCallback(
    async (tab, page, limit, searchVal, from, to) => {
      setLoading(true);
      setError(null);
      try {
        const params = { tab, page, limit };
        if (searchVal) params.search = searchVal;
        if (from) params.date_from = from;
        if (to) params.date_to = to;

        const res = await api.get('/approvals', {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotifications(res.data.notifications || []);
        setPagination(res.data.pagination || { total: 0, total_pages: 1 });
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load notifications');
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // Re-fetch whenever tab, page, limit, or date filters change immediately
  useEffect(() => {
    fetchNotifications(activeTab, currentPage, itemsPerPage, search, dateFrom, dateTo);
  }, [activeTab, currentPage, itemsPerPage, dateFrom, dateTo, fetchNotifications]);

  // Debounce search input to avoid hammering the API
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setCurrentPage(1);
      fetchNotifications(activeTab, 1, itemsPerPage, search, dateFrom, dateTo);
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]); // eslint-disable-line

  const handleTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const params = { tab: activeTab, page: 1, limit: 9999 };
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/approvals', {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const allData = res.data.notifications || [];

      const headerMaps = {
        waiting: {
          project_job_code: 'Job Code',
          project_title: 'Project Title',
          approval_type: 'Approval Type',
          started_by_user_name: 'Started By',
          started_at: 'Started At',
        },
        approved: {
          project_job_code: 'Job Code',
          project_title: 'Project Title',
          approval_type: 'Approval Type',
          started_by_user_name: 'Started By',
          started_at: 'Started At',
          approved_by_name: 'Approved By',
          approved_at: 'Approved At',
        },
        rejected: {
          project_job_code: 'Job Code',
          project_title: 'Project Title',
          approval_type: 'Approval Type',
          started_by_user_name: 'Started By',
          started_at: 'Started At',
          rejected_by_name: 'Rejected By',
          rejected_at: 'Rejected At',
          rejection_reason: 'Reason',
        },
        recent: {
          project_job_code: 'Job Code',
          project_title: 'Project Title',
          approval_type: 'Approval Type',
          started_by_user_name: 'Started By',
          started_at: 'Started At',
          decision: 'Decision',
          completed_by_name: 'Completed By',
          approved_by_name: 'Approved By',
          approved_at: 'Approved At',
          task_completed_at: 'Completed At',
        },
      };

      const formatted = formatDataForExport(allData, headerMaps[activeTab] || headerMaps.waiting);
      const tabLabel = TABS.find((t) => t.key === activeTab)?.label || activeTab;
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(formatted, `approvals_${tabLabel.replace(/\s+/g, '_')}_${dateStr}.csv`);
    } catch (err) {
      setError('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  // Return a datetime-local string defaulting to now (local time, no seconds).
  const nowDatetimeLocal = () => {
    const d = new Date();
    d.setSeconds(0, 0);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const openApproveModal = (id) => setApproveModal({ open: true, id, datetime: nowDatetimeLocal() });
  const closeApproveModal = () => setApproveModal({ open: false, id: null, datetime: '' });

  const handleApproveSubmit = async () => {
    if (!approveModal.datetime) return;
    setActionLoading(approveModal.id);
    try {
      await api.post(
        `/approvals/${approveModal.id}/approve`,
        { approved_at: new Date(approveModal.datetime).toISOString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      closeApproveModal();
      await fetchNotifications(activeTab, currentPage, itemsPerPage, search, dateFrom, dateTo);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  // External error categories only — this dialog is for external (client) approvals.
  const fetchErrorCategories = useCallback(async () => {
    try {
      setErrorCategoriesLoading(true);
      const res = await api.get('/admin/config/error-categories', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = Array.isArray(res.data) ? res.data : [];
      setErrorCategories(raw.map((cat) => ({ ...cat, type: cat.type || 'internal' })));
    } catch (err) {
      setErrorCategories([]);
    } finally {
      setErrorCategoriesLoading(false);
    }
  }, [token]);

  const toggleRejectCategory = (categoryId, categoryName) => {
    setRejectModal((prev) => {
      const existing = Array.isArray(prev.externalCategories) ? prev.externalCategories : [];
      const isSelected = existing.some((c) => c === categoryId || c === categoryName);
      return {
        ...prev,
        externalCategories: isSelected
          ? existing.filter((c) => c !== categoryId && c !== categoryName)
          : [...existing, categoryId || categoryName],
      };
    });
  };

  const openRejectModal = (n) => {
    setRejectModal({ open: true, id: n.id, task_id: n.task_id, reason: '', datetime: nowDatetimeLocal(), externalCategories: [] });
    fetchErrorCategories();
  };
  const closeRejectModal = () => setRejectModal({ open: false, id: null, task_id: null, reason: '', datetime: '', externalCategories: [] });

  const handleRejectSubmit = async () => {
    if (!rejectModal.reason.trim() || !rejectModal.datetime) return;
    const externalCategories = Array.isArray(rejectModal.externalCategories) ? rejectModal.externalCategories : [];
    if (rejectModal.task_id && externalCategories.length === 0) {
      setError('Please select at least one external error category.');
      return;
    }
    setActionLoading(rejectModal.id);
    try {
      if (rejectModal.task_id) {
        // Full rejection: rejects the task AND creates the rework round (same as
        // Active Projects → approval task → reject). This also marks the
        // approval notification rejected internally.
        await api.post(
          '/lead/reject-approval',
          {
            task_id: rejectModal.task_id,
            reason: rejectModal.reason.trim(),
            external_error_categories: externalCategories,
            error_categories: externalCategories,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Fallback: notification-only reject (no linked task).
        await api.post(
          `/approvals/${rejectModal.id}/reject`,
          { reason: rejectModal.reason.trim(), rejected_at: new Date(rejectModal.datetime).toISOString() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      closeRejectModal();
      await fetchNotifications(activeTab, currentPage, itemsPerPage, search, dateFrom, dateTo);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = pagination.total_pages || 1;
  const totalCount = pagination.total || 0;

  const renderPageButtons = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);

    if (left > 1) {
      pages.push(1);
      if (left > 2) pages.push('...');
    }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) {
      if (right < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return pages.map((p, idx) =>
      p === '...' ? (
        <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-500 text-sm">...</span>
      ) : (
        <button
          key={p}
          onClick={() => setCurrentPage(p)}
          className={`px-3 py-1 rounded text-sm font-medium border transition-colors ${
            p === currentPage
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          {p}
        </button>
      )
    );
  };

  return (
    <div className="p-6">
      {/* Section Tabs */}
      <div className="flex items-end justify-between mb-6 border-b border-gray-200">
        <div className="flex gap-1">
          {SECTION_TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setSectionTab(key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors -mb-px ${
                sectionTab === key
                  ? 'border-indigo-600 text-indigo-700 bg-indigo-50 rounded-t-lg'
                  : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
        {sectionTab === 'approvals' && (
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={handleExportCSV}
              disabled={exportLoading}
              className="flex items-center px-3 py-2 text-sm text-white bg-green-600 border border-green-700 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-4 h-4 mr-1" />
              {exportLoading ? 'Exporting...' : 'Export CSV'}
            </button>
            <button
              onClick={() => fetchNotifications(activeTab, currentPage, itemsPerPage, search, dateFrom, dateTo)}
              className="flex items-center px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </button>
          </div>
        )}
      </div>

      {sectionTab === 'prepress' && <TaskTrackerSection taskName="Prepress , QC (self QC)" token={token} />}
      {sectionTab === 'ripqc' && <TaskTrackerSection taskName="RIP QC" token={token} />}
      {sectionTab === 'overall' && <OverallStatusSection token={token} />}

      {sectionTab === 'approvals' && (
      <>
      {error && (
        <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 flex items-center rounded">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tab Bar */}
      <div className="flex space-x-2 mb-4 border-b border-gray-200">
        {TABS.map(({ key, label, icon: Icon, color }) => (
          <button
            key={key}
            onClick={() => handleTabChange(key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === key
                ? 'border-blue-600 text-blue-600'
                : `border-transparent ${color} hover:border-gray-300`
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Filter Bar */}
      <div className={`mb-4 p-3 rounded-lg border flex flex-wrap items-center gap-3 ${hasFilters ? 'bg-sky-100 border-sky-300' : 'bg-white border-gray-200'}`}>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-2 text-sm text-white bg-red-500 hover:bg-red-700 rounded-md transition-colors"
          >
            Clear
          </button>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search job code, project, client, user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[260px]"
          />
        </div>
        <label className="text-sm text-gray-600 whitespace-nowrap">From:</label>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <label className="text-sm text-gray-600 whitespace-nowrap">To:</label>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 font-medium">Job Code</th>
                  <th className="px-6 py-3 font-medium">Approval Type</th>
                  <th className="px-6 py-3 font-medium">Started By</th>
                  <th className="px-6 py-3 font-medium">Started At</th>

                  {activeTab === 'approved' && (
                    <>
                      <th className="px-6 py-3 font-medium">Approved By</th>
                      <th className="px-6 py-3 font-medium">Approved At</th>
                    </>
                  )}

                  {activeTab === 'rejected' && (
                    <>
                      <th className="px-6 py-3 font-medium">Rejected By</th>
                      <th className="px-6 py-3 font-medium">Rejected At</th>
                      <th className="px-6 py-3 font-medium">Reason</th>
                    </>
                  )}

                  {activeTab === 'recent' && (
                    <>
                      <th className="px-6 py-3 font-medium">Decision</th>
                      <th className="px-6 py-3 font-medium">Completed By</th>
                      <th className="px-6 py-3 font-medium">Approved By</th>
                      <th className="px-6 py-3 font-medium">Approved At</th>
                      <th className="px-6 py-3 font-medium">Completed At</th>
                    </>
                  )}

                  {activeTab === 'waiting' && canApproveReject && (
                    <th className="px-6 py-3 font-medium text-right">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {notifications.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-16 text-center text-gray-500">
                      No entries found for this tab.
                    </td>
                  </tr>
                ) : (
                  notifications.map((n) => (
                    <tr key={n.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-mono font-medium text-gray-700">
                        {n.project_job_code || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium whitespace-nowrap">
                          {n.approval_type || n.task_name || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{n.started_by_user_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(n.started_at)}
                      </td>

                      {activeTab === 'approved' && (
                        <>
                          <td className="px-6 py-4 text-sm text-gray-700">{n.approved_by_name || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                            {formatDate(n.approved_at)}
                          </td>
                        </>
                      )}

                      {activeTab === 'rejected' && (
                        <>
                          <td className="px-6 py-4 text-sm text-gray-700">{n.rejected_by_name || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                            {formatDate(n.rejected_at)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 max-w-xs" title={n.rejection_reason}>
                            <span className="truncate block max-w-[200px]">{n.rejection_reason || '-'}</span>
                          </td>
                        </>
                      )}

                      {activeTab === 'recent' && (
                        <>
                          <td className="px-6 py-4 text-sm">
                            {n.decision === 'approved' ? (
                              <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                <CheckCircle className="w-3 h-3 mr-1" /> Approved
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                <XCircle className="w-3 h-3 mr-1" /> Rejected
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">{n.completed_by_name || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{n.approved_by_name || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                            {formatDate(n.approved_at)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                            {formatDate(n.task_completed_at)}
                          </td>
                        </>
                      )}

                      {activeTab === 'waiting' && canApproveReject && (
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openApproveModal(n.id)}
                              disabled={actionLoading === n.id}
                              className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />
                              Approve
                            </button>
                            <button
                              onClick={() => openRejectModal(n)}
                              disabled={actionLoading === n.id}
                              className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <XCircle className="w-3.5 h-3.5 mr-1" />
                              Reject
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span className="text-sm text-gray-500">
                {totalCount > 0
                  ? `${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, totalCount)} of ${totalCount}`
                  : '0 results'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                «
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ‹
              </button>
              {renderPageButtons()}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ›
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                »
              </button>
            </div>
          </div>
        </div>
      )}

      </>
      )}

      {/* Reject Modal */}
      {rejectModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Reject Approval</h3>
              <button onClick={closeRejectModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rejected At <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={rejectModal.datetime}
                  onChange={(e) => setRejectModal((prev) => ({ ...prev, datetime: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                />
              </div>
              {rejectModal.task_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    External Error Categories <span className="text-red-500">*</span>
                  </label>
                  {errorCategoriesLoading ? (
                    <div className="text-xs text-gray-500 py-2">Loading categories…</div>
                  ) : errorCategories.filter((c) => (c.type || 'internal') === 'external').length === 0 ? (
                    <div className="text-xs text-gray-500 py-2 px-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      No external error categories configured. Please add them in Admin Settings.
                    </div>
                  ) : (
                    <div className="border border-gray-300 rounded-md p-3 space-y-2 max-h-40 overflow-y-auto bg-gray-50">
                      {errorCategories
                        .filter((c) => (c.type || 'internal') === 'external')
                        .map((category) => {
                          const categoryId = category.id || category._id;
                          const isSelected =
                            (rejectModal.externalCategories || []).includes(categoryId) ||
                            (rejectModal.externalCategories || []).includes(category.name);
                          return (
                            <label key={categoryId} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-2 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleRejectCategory(categoryId, category.name)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">{category.name}</span>
                            </label>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Rejection <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectModal.reason}
                  onChange={(e) =>
                    setRejectModal((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  placeholder="Describe why this approval is being rejected..."
                  autoFocus
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeRejectModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={
                  !rejectModal.reason.trim() ||
                  !rejectModal.datetime ||
                  (rejectModal.task_id && (rejectModal.externalCategories || []).length === 0) ||
                  actionLoading === rejectModal.id
                }
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {approveModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Approve</h3>
              <button onClick={closeApproveModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Approved At <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={approveModal.datetime}
                onChange={(e) => setApproveModal((prev) => ({ ...prev, datetime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeApproveModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleApproveSubmit}
                disabled={!approveModal.datetime || actionLoading === approveModal.id}
                className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                <CheckCircle className="w-4 h-4 mr-1 inline" />
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnApprovals;
